import { FrameSelectorFieldMui } from '@app/components/form/FrameSelectorFieldMui';
import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import { TitleAndContentFieldsMui } from '@app/modules/social-export/components/inputs/TitleAndContentFieldsMui';
import {
  getBaseExportSchema,
  MessageFormatter,
} from '@app/modules/social-export/components/validation/base-export-schema';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/useSocialExportFactory';
import { FormSection } from '@app/modules/social-export/screens/manage/platforms/FormSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { socialExport } from '@mi-front/api-clients';
import { Box, Grid } from '@mui/material';
import { useMemo } from 'react';
import { FormContainer, SwitchElement, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { DailymotionCategoryField } from './DailymotionCategoryField';

type DailymotionPublishFormProps = BaseExportFormProps<
  socialExport.DailymotionPublishDto,
  socialExport.DailymotionSettingsDto
>;

const requiredMsg = { id: 'common.required', defaultMessage: 'Requerido' };

function getDailymotionPublishValidationSchema(t: MessageFormatter) {
  return getBaseExportSchema(t).extend({
    title: z.string().trim().min(1, t(requiredMsg)),
    content: z.string().trim().min(1, t(requiredMsg)),
    tags: z.string().optional(),
    forKids: z.boolean(),
    private: z.boolean(),
    thumbOffsetMs: z.number().nullable().optional(),
    channel: z
      .string()
      .nullable()
      .optional()
      .transform((val) => (val === '' ? null : val)),
  });
}

type DailymotionPublishValues = z.infer<ReturnType<typeof getDailymotionPublishValidationSchema>>;

export function DailymotionPublishForm({
  fragment,
  onUpload,
  defaultSettings,
  actions,
}: DailymotionPublishFormProps) {
  const { $t } = useIntl();
  const schema = useMemo(() => getDailymotionPublishValidationSchema((msg) => $t(msg)), [$t]);
  const initialValues: DailymotionPublishValues = {
    title: fragment.title,
    content: fragment.description ?? '',
    tags: fragment.tags?.join(', ') ?? '',
    forKids: defaultSettings?.forKids ?? false,
    private: defaultSettings?.private ?? true,
    channel: defaultSettings?.channel ?? null,
    thumbOffsetMs: null,
    publishAt: null,
  };

  const beforeSubmit = (data: DailymotionPublishValues): socialExport.DailymotionPublishDto => ({
    title: data.title,
    description: data.content,
    tags: data.tags
      ? data.tags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    forKids: data.forKids,
    private: data.private,
    thumbOffsetMs: data.thumbOffsetMs ?? undefined,
    publishAt: data.publishAt || undefined,
    channel: (data.channel as socialExport.DailymotionPublishDtoChannelEnumKey) || undefined,
  });

  return (
    <FormContainer
      values={initialValues}
      resolver={zodResolver(schema)}
      onSuccess={async (formData) => {
        await onUpload(beforeSubmit(formData));
      }}
    >
      <FormSection
        title={$t({
          id: 'socialExport.form.content',
          defaultMessage: 'Contenido',
        })}
      >
        <TitleAndContentFieldsMui />
        <Grid container>
          <Grid item md={8}>
            <TextFieldElement
              name="tags"
              label={$t({
                id: 'socialExport.form.tags',
                defaultMessage: 'Tags',
              })}
              helperText={$t({
                id: 'socialExport.form.commaSeparatedValues',
                defaultMessage: 'Valores separados por coma',
              })}
              fullWidth
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection
        title={$t({
          id: 'socialExport.form.settings',
          defaultMessage: 'Configuración',
        })}
      >
        <Grid container>
          <Grid item md={8}>
            <DailymotionCategoryField />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={6}>
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2}>
              <SwitchElement
                name="forKids"
                label={$t({
                  id: 'socialExport.form.forKids',
                  defaultMessage: 'Contenido apto para niños',
                })}
                color="primary"
              />
              <SwitchElement
                name="private"
                label={$t({
                  id: 'socialExport.form.uploadAsPrivate',
                  defaultMessage: 'Cargar como video privado',
                })}
                color="primary"
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={6}>
            <FrameSelectorFieldMui name="thumbOffsetMs" unit="ms" />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection
        title={$t({
          id: 'socialExport.form.schedule',
          defaultMessage: 'Programación',
        })}
      >
        <Grid container>
          <Grid item md={12}>
            <SocialPublishAtField />
          </Grid>
        </Grid>
      </FormSection>
      {actions}
    </FormContainer>
  );
}
